import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Chip, Drawer, Stack, Typography } from '@mui/material'
import isEmpty from 'lodash/isEmpty'
import React, { useCallback } from 'react'
import {
  type SetValueConfig,
  useForm,
  useFormContext,
  useWatch
} from 'react-hook-form'
import { object as yupObject } from 'yup'

import { AddTagsForm } from 'components/common/AddTagsForm'
import { ButtonWithConfirmation } from 'components/common/ButtonWithConfirmation'
import { ChipListMultiValue } from 'components/common/chips/ChipList'
import { LoadingButton } from 'components/common/LoadingButton'
import { MUIExternalLink } from 'components/common/MUIExternalLink'
import Icon from 'components/Icon'
import {
  validateServiceTagName,
  validateServiceTagValues
} from 'components/services/service-tag-utils'
import { PNI_AGENT_MATCH_ERROR_FALLBACK_MESSAGE } from 'components/status-checks/edit/pni-agents-matching-tags-utils'
import { PniAgentsMatchingTagsSection } from 'components/status-checks/edit/PniAgentsMatchingTagsSection'
import {
  formatStatusCheckTag,
  type StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { GREMLIN_COM_DOCS_LINKS } from 'constants/external-urls-gremlin-docs'
import { usePniMultiSelectTags } from 'hooks/use-pni-multi-select-tags'
import { usePopoverState } from 'hooks/use-popover-state'
import toast, { errorToast } from 'lib/toast'
import { useGetStatusChecks } from 'queries/status-checks/use-get-status-checks'
import { usePutStatusCheckTags } from 'queries/status-checks/use-put-status-check-tags'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { getMultiSelectTagsSchema } from 'utils/yup-additions'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'
import type { MultiValueTags } from 'types/tags'

export type TagsFormValues = Pick<StatusCheckFormValues, 'multiSelectTags'>

type SharedProps = {
  isPNI: boolean
  monitorTags?: string[]
}

// create flow
type WithInitialTags = SharedProps & {
  initialTags: MultiValueTags
  statusCheck?: never
}

// edit flow
type WithStatusCheck = SharedProps & {
  initialTags?: never
  statusCheck: ServiceStatusCheckFromStatusCheck
}

type EditTagsStatusCheckTagsDrawerProps = WithStatusCheck | WithInitialTags

const tagsFormValidationSchema = yupObject({
  multiSelectTags: getMultiSelectTagsSchema(false)
})

const SUCCESS_MESSAGE_CREATE =
  'Tags updated for this Health Check, save Health Check to apply them.'
const SUCCESS_MESSAGE_EDIT = 'Successfully updated tags for this Health Check.'

const BUTTON_CANCEL_COMMON_PROPS = {
  children: 'Cancel',
  color: 'secondary',
  variant: 'contained'
} as const

function displayTagsSaveErrorToast(err?: string) {
  return errorToast(
    err && err.length
      ? err
      : 'There was an issue saving tags for this Health Check. Please try again later.'
  )
}

function displayMatchingAgentsErrorToast(err?: string) {
  return errorToast(
    err && err.length ? err : PNI_AGENT_MATCH_ERROR_FALLBACK_MESSAGE
  )
}

export const EditTagsStatusCheckTagsDrawer: React.FC<
  EditTagsStatusCheckTagsDrawerProps
> = ({ initialTags, isPNI, monitorTags, statusCheck }) => {
  const initialMultiSelectTags = initialTags ?? statusCheck?.multiSelectTags

  const drawerState = usePopoverState()

  const { refetch: refetchStatusChecks } = useGetStatusChecks({
    enabled: false
  })

  const { mutate: updateStatusCheckTags, isLoading } = usePutStatusCheckTags(
    statusCheck?.id
  )

  const statusCheckForm = useFormContext<StatusCheckFormValues>()

  const form = useForm<TagsFormValues>({
    defaultValues: {
      multiSelectTags: initialMultiSelectTags
    },
    mode: 'all',
    resolver: yupResolver(tagsFormValidationSchema)
  })
  const { control, formState, reset, setValue } = form
  const { isDirty, isSubmitting, isValid } = formState
  const multiSelectTagsValue = useWatch({ control, name: 'multiSelectTags' })

  const handleReset = useCallback(
    (values: TagsFormValues['multiSelectTags'] = initialMultiSelectTags) => {
      reset({ multiSelectTags: values })
    },
    [reset, initialMultiSelectTags]
  )

  const setTagsValue = useCallback(
    (values: TagsFormValues['multiSelectTags'], options?: SetValueConfig) => {
      setValue('multiSelectTags', values, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
        ...options
      })
    },
    [setValue]
  )

  const {
    agentsMatchingTags,
    handleAddTag,
    handleDeleteTag,
    isLoadingCheckMatchingTags,
    matchingAgentsError,
    tagNames,
    tags,
    tagValues
  } = usePniMultiSelectTags({
    multiSelectTagsValue,
    onSetValue: setTagsValue,
    onLastTagDeleted: () => {
      if (isEmpty(initialMultiSelectTags)) {
        // no initial tags, deleting the last added tag returns us to the
        // original state, so reset the form to clean (not dirty)
        handleReset()
      } else {
        // had initial tags, clearing them is an actual change,
        // set to {} and keep the form dirty
        setTagsValue({})
      }
    },
    pniOptions: {
      enabled: drawerState.isOpen && isPNI,
      onError: displayMatchingAgentsErrorToast
    }
  })

  const handleCloseOnCancel = () => {
    if (isDirty) {
      handleReset()
    }

    drawerState.close()
  }

  const updateTags = async () => {
    if (!isDirty) {
      return
    }

    if (statusCheck) {
      updateStatusCheckTags(multiSelectTagsValue ?? {}, {
        onError(err) {
          displayTagsSaveErrorToast(messageFromResponseError(err))
        },
        async onSuccess() {
          drawerState.close()
          toast({ text: SUCCESS_MESSAGE_EDIT, type: 'success' })
          handleReset(multiSelectTagsValue)

          // Refresh status checks list
          await handleDataFetchOnChange(refetchStatusChecks)

          /*
           * Make sure the main form has up to date tags
           * so that any further edits are saved properly
           */
          statusCheckForm.setValue('multiSelectTags', multiSelectTagsValue)
          statusCheckForm.resetField('multiSelectTags', {
            defaultValue: multiSelectTagsValue
          })
        }
      })
    } else {
      drawerState.close()
      toast({ text: SUCCESS_MESSAGE_CREATE, type: 'success' })
      handleReset(multiSelectTagsValue)

      /*
       * Make sure the main form has up to date tags
       * so that any further edits are saved properly
       */
      statusCheckForm.setValue('multiSelectTags', multiSelectTagsValue)
      statusCheckForm.resetField('multiSelectTags', {
        defaultValue: multiSelectTagsValue
      })
    }
  }

  const isSavingTags = isSubmitting || isLoading
  const isSavingTagsDisabled = !isDirty || !isValid

  return (
    <>
      <Button
        size="small"
        startIcon={<Icon css={{ width: '.9rem' }} icon="pencil" />}
        variant="contained"
        onClick={drawerState.open}
      >
        Edit
      </Button>
      <Drawer
        anchor="right"
        open={drawerState.isOpen}
        PaperProps={{
          sx: {
            padding: 6,
            height: '100%',
            width: 800
          }
        }}
        {...(!isDirty && { onClose: drawerState.close })}
      >
        <Stack sx={{ rowGap: 3 }}>
          <Typography component="h2" variant="h6">
            Edit Tags
          </Typography>
          <Typography>
            Health Checks tags can be used to annotate identifying information.
            Tags can also be used to help{' '}
            <MUIExternalLink
              href={GREMLIN_COM_DOCS_LINKS.ROUTING_HEALTH_CHECKS}
            >
              route PNIs
            </MUIExternalLink>
            {'. '}
            Tags that come from the observability tool cannot be modified.
          </Typography>
          <AddTagsForm
            tagNames={tagNames}
            tagValues={tagValues}
            // Note: validation matches the service tag validation
            validateTagName={validateServiceTagName}
            validateTagValues={validateServiceTagValues}
            onAdd={handleAddTag}
          />
          {/* ChipLists wrapper - status check and monitor tags */}
          <Stack
            direction="row"
            sx={[
              {
                alignItems: 'center',
                columnGap: 1,
                flexWrap: 'wrap',
                rowGap: 1
              },
              (muiTheme) => ({ minHeight: muiTheme.spacing(4) })
            ]}
          >
            <ChipListMultiValue
              size="medium"
              tags={tags}
              onDeleteTag={handleDeleteTag}
            />
            {
              /* Note: these are read-only */
              monitorTags?.map((tag) => {
                return (
                  <Chip
                    key={tag}
                    label={formatStatusCheckTag(tag)}
                    role="status"
                  />
                )
              })
            }
          </Stack>
          {/* PNI Agents matching current tags */}
          {isPNI &&
            (isLoadingCheckMatchingTags ? (
              <Typography sx={{ minHeight: 52, paddingTop: 2 }} variant="body2">
                Loading matching PNI agents...
              </Typography>
            ) : (
              <PniAgentsMatchingTagsSection
                agentsMatchingTags={agentsMatchingTags}
                multiSelectTags={multiSelectTagsValue}
                {...(matchingAgentsError && {
                  errorMessage: matchingAgentsError
                })}
              />
            ))}
          {/* Drawer actions */}
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              columnGap: 1
            }}
          >
            <LoadingButton
              data-testid="edit-status-check-save-tags-button"
              isDisabled={isSavingTagsDisabled}
              isLoading={isSavingTags}
              variant="contained"
              onClick={updateTags}
            >
              {isSavingTags ? ' Saving...' : 'Save'}
            </LoadingButton>
            {isDirty ? (
              <ButtonWithConfirmation
                {...BUTTON_CANCEL_COMMON_PROPS}
                buttonCancelText="Keep Editing"
                buttonConfirmText="Discard"
                dialogBodyContent="Do you want to discard unsaved tag changes?"
                dialogTitleContent="Unsaved tag changes"
                onConfirm={handleCloseOnCancel}
              />
            ) : (
              <Button
                {...BUTTON_CANCEL_COMMON_PROPS}
                onClick={handleCloseOnCancel}
              />
            )}
          </Stack>
        </Stack>
      </Drawer>
    </>
  )
}
